import { t } from '@/i18n';
import { z } from 'zod';

/// ////////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////////

export const contractMethod = z.enum([
  'open-tendering-lowest-price',
  'open-tendering-comprehensive-evaluation',
  'selective-tendering-lowest-price',
  'selective-tendering-comprehensive-evaluation',
  'negotiated-contract-competitive-bidding',
  'negotiated-contract-public-offering',
  'negotiated-contract-small-amount',
  'negotiated-contract-unsuccessful',
  'negotiated-contract-others',
  'subsidy',
  'management-expense-grant',
  'act-bearing-national-treasury-liabilities',
  'others',
]);
const _contractMethodOnContractor = contractMethod.exclude([
  'management-expense-grant',
  'act-bearing-national-treasury-liabilities',
]);

export const ContractMethodLabelModifier = {
  'open-tendering-lowest-price': t(
    'contract-method-open-tendering-lowest-price',
  ),
  'open-tendering-comprehensive-evaluation': t(
    'contract-method-open-tendering-comprehensive-evaluation',
  ),
  'selective-tendering-lowest-price': t(
    'contract-method-selective-tendering-lowest-price',
  ),
  'selective-tendering-comprehensive-evaluation': t(
    'contract-method-selective-tendering-comprehensive-evaluation',
  ),
  'negotiated-contract-competitive-bidding': t(
    'contract-method-negotiated-contract-competitive-bidding',
  ),
  'negotiated-contract-public-offering': t(
    'contract-method-negotiated-contract-public-offering',
  ),
  'negotiated-contract-small-amount': t(
    'contract-method-negotiated-contract-small-amount',
  ),
  'negotiated-contract-unsuccessful': t(
    'contract-method-negotiated-contract-unsuccessful',
  ),
  'negotiated-contract-others': t('contract-method-negotiated-contract-others'),
  subsidy: t('contract-method-subsidy'),
  'management-expense-grant': t('contract-method-management-expense-grant'),
  'act-bearing-national-treasury-liabilities': t(
    'contract-method-act-bearing-national-treasury-liabilities',
  ),
  others: t('contract-method-others'),
} as const satisfies Record<z.infer<typeof contractMethod>, string>;

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

const PaymentAmountBreakdown = z.object({
  name: z.string().min(1),
  purpose: z.string().min(1),
  amount: z.number().int().nullish(),
});

const PaymentContractModel = z.object({
  id: z.string().uuid(),
  overview: z.string().min(1),
  isOthers: z.boolean(),
  amount: z.number().int(),
  contractMethod: contractMethod.nullish(),
  contractMethodDescription: z.string().nullish(),
  numberOfApplicants: z.number().max(999_999_999).int().nullish(),
  bidRate: z.number().nullish(),
  reason: z.string().nullish(),
  amountBreakdown: PaymentAmountBreakdown.array().optional(),
  paymentId: z.string().uuid(),
  hideBidRate: z.boolean(),
});

export const PaymentContractsModel = PaymentContractModel.array();

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

const _PaymentContractFormSchema = PaymentContractModel.pick({
  overview: true,
  amount: true,
  contractMethodDescription: true,
  numberOfApplicants: true,
  bidRate: true,
  reason: true,
  amountBreakdown: true,
  paymentId: true,
})
  .extend({
    contractMethod: contractMethod,
    isOthers: PaymentContractModel.shape.isOthers.optional(),
    hideBidRate: z.boolean(),
  })
  .refine(
    (data) =>
      !(
        (data.contractMethod === 'others' ||
          data.contractMethod === 'negotiated-contract-others') &&
        !data.contractMethodDescription
      ),
    {
      path: ['contractMethodDescription'],
      message: t('validation.required'),
    },
  );

const _PaymentContractMutationParamsSchema = PaymentContractModel.pick({
  overview: true,
  amount: true,
  contractMethod: true,
  contractMethodDescription: true,
  numberOfApplicants: true,
  bidRate: true,
  reason: true,
  amountBreakdown: true,
  paymentId: true,
  hideBidRate: true,
}).extend({
  projectId: z.string().uuid(),
  paymentGroupId: z.string().uuid(),
  isOthers: PaymentContractModel.shape.isOthers.optional(),
});
